import { format } from 'date-fns';
import { FC, useLayoutEffect, useRef, useState } from 'react';

import Button, { ButtonSize, ButtonVariant } from 'src/components/atoms/Button';
import Text, { TextVariant } from 'src/components/atoms/Text';
import Modal, { ModalVariant } from 'src/components/organisms/Modal';
import { CalculationNote, CalculationNoteOrigin } from 'src/features/incomeVerification/api';

import {
  ClampedNoteText,
  Count,
  Dot,
  Hint,
  NoteMeta,
  NoteText,
  NotesCard,
  NotesCardHeader,
  NotesCardTitle,
  OriginChip,
  ReadOnlyBadge,
  Timeline,
  TimelineItem
} from './styles';

type CalculationNotesCardProps = { notes: CalculationNote[] };

type NoteMetaLineProps = { note: CalculationNote };

type TimelineNoteProps = { note: CalculationNote; onViewMore: () => void };

const ORIGIN_LABEL: Record<CalculationNoteOrigin, string> = {
  SPECIALTY: 'Specialty',
  DISPUTE: 'Dispute'
};

const NoteMetaLine: FC<NoteMetaLineProps> = ({ note }) => (
  <NoteMeta>
    <OriginChip origin={note.origin}>{ORIGIN_LABEL[note.origin]}</OriginChip>
    <Hint>
      {`${format(new Date(note.created_at), 'MMM d, yyyy')} · ${
        note.author_name ?? 'Unknown analyst'
      }`}
    </Hint>
  </NoteMeta>
);

const TimelineNote: FC<TimelineNoteProps> = ({ note, onViewMore }) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isClamped, setIsClamped] = useState(false);

  useLayoutEffect(() => {
    const element = textRef.current;
    if (!element) return;
    const measure = () => setIsClamped(element.scrollHeight > element.clientHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [note.text]);

  return (
    <TimelineItem data-testid="calculation-note">
      <Dot origin={note.origin} aria-hidden="true" />
      <div>
        <NoteMetaLine note={note} />
        <ClampedNoteText ref={textRef}>{note.text}</ClampedNoteText>
        {isClamped && (
          <Button
            name="button-view-calculation-note"
            variant={ButtonVariant.ghost}
            size={ButtonSize.small}
            onClick={onViewMore}
          >
            View more
          </Button>
        )}
      </div>
    </TimelineItem>
  );
};

const CalculationNotesCard: FC<CalculationNotesCardProps> = ({ notes }) => {
  const [selectedNote, setSelectedNote] = useState<CalculationNote | null>(null);

  return (
    <NotesCard data-testid="calculation-notes">
      <NotesCardHeader>
        <NotesCardTitle>
          <Text variant={TextVariant.h4}>Calculation notes</Text>
          <Count>{notes.length}</Count>
        </NotesCardTitle>
        <ReadOnlyBadge>Read-only</ReadOnlyBadge>
      </NotesCardHeader>
      <Timeline>
        {notes.map((note, index) => (
          <TimelineNote key={index} note={note} onViewMore={() => setSelectedNote(note)} />
        ))}
      </Timeline>
      {selectedNote && (
        <Modal
          title="Calculation note"
          variant={ModalVariant.none}
          showModal
          setShowModal={() => setSelectedNote(null)}
          showCloseButton
          showModalActions={false}
          scrollableContent
        >
          <div data-testid="calculation-note-full">
            <NoteMetaLine note={selectedNote} />
            <NoteText>{selectedNote.text}</NoteText>
          </div>
        </Modal>
      )}
    </NotesCard>
  );
};

export default CalculationNotesCard;
